import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseComponent } from 'src/app/base/base.component';
import { DashboardVisualTarget } from 'src/app/pbir/dashboards/DashboardVisualTarget';
import { SnackService } from 'src/app/services/snack.service';
import { AppUser } from 'src/app/shared/models/app-user';
import { combineForLoading, combineLogicalOr } from 'src/app/shared/utils';
import * as AppSelectors from 'src/app/state/app/app.selectors';
import * as HomeActions from 'src/app/state/home/home.actions';
import * as HomeSelectors from 'src/app/state/home/home.selectors';
import { DashboardDto, DashboardVisualContentDto, FilterDto, ListUserDashboardDto, ListUserVisualDto, UserStateDto, VisualDto } from '../../services/api.services';
import { AppActions } from '../../state/app';

@Component({
  selector: 'app-insights-user-home-shell',
  template: `
    <app-insights-user-home
      [user]="user$ | async"
      [userState]="userState$ | async"

      [busy]="busy$ | async"
      [loading]="loading$ | async"

      [loadingDashboards]="loadingDashboards$ | async"
      [dashboards]="dashboards$ | async"

      [loadingVisuals]="loadingVisuals$ | async"
      [visuals]="visuals$ | async"

      [dashboardId]="dashboardId$ | async"
      [visualId]="visualId$ | async"

      [loadingDashboard]="loadingDashboard$ | async"
      [dashboard]="dashboard$ | async"
      [visualContents]="visualContents$ | async"

      [loadingVisual]="loadingVisual$ | async"
      [visual]="visual$ | async"
      (loadVisualEvent)="loadVisual($event)"
      (addVisualToHomeEvent)="addVisualToHome($event)"
      (copyVisualEvent)="copyVisual($event)"

      (editDashboardEvent)="editDashboard($event)"
      (loadDashboardEvent)="loadDashboard($event)"
      (copyDashboardEvent)="copyDashboard($event)"
      (addDashboardToHomeEvent)="addDashboardToHome($event)"

      (exportToPdf)="exportToPdf($event)"
      (exportVisual)="exportVisual()"
    ></app-insights-user-home>
  `,
  standalone: false
})
export class InsightsUserHomeShellComponent extends BaseComponent implements OnInit, OnDestroy {
  static readonly pageId = 'InsightsUserHomeShellComponent';

  user$: Observable<AppUser>;

  loadingUserState$: Observable<boolean>;
  userState$: Observable<UserStateDto>;

  busy$: Observable<boolean>;
  loading$: Observable<boolean>;

  loaded$: Observable<boolean>;

  loadingDashboards$: Observable<boolean>;
  dashboards$: Observable<ListUserDashboardDto[]>;

  loadingVisuals$: Observable<boolean>;
  visuals$: Observable<ListUserVisualDto[]>

  dashboardId$: Observable<number>;

  loadingDashboard$: Observable<boolean>;
  dashboard$: Observable<DashboardDto>;
  visualContents$: Observable<DashboardVisualContentDto[]>;

  visualId$: Observable<number>;

  loadingVisual$: Observable<boolean>;
  visual$: Observable<VisualDto>;

  copyingDashboard$: Observable<boolean>;
  addingDashboard$: Observable<boolean>;

  focusedVisual$: Observable<DashboardVisualTarget>;
  hasFocusedVisual$: Observable<boolean>;

  constructor(
    public store: Store,
    public router: Router,
    public route: ActivatedRoute,
    public snackService: SnackService) {
    super(store, router, route, snackService);
  }

  ngOnInit() {
    this.store.dispatch(AppActions.setPageComponent({ component: InsightsUserHomeShellComponent.pageId }));


    super.ngOnInit();

    this.user$ = this.store.pipe(select(AppSelectors.getUser));

    this.loadingDashboards$ = this.store.select(HomeSelectors.isLoadingDashboards);
    this.dashboards$ = this.store.select(HomeSelectors.getDashboards);

    this.loadingVisuals$ = this.store.select(HomeSelectors.isLoadingVisuals)
    this.visuals$ = this.store.select(HomeSelectors.getVisuals)

    this.dashboardId$ = this.store.pipe(select(HomeSelectors.getDashboardId));
    this.loadingDashboard$ = this.store.pipe(select(HomeSelectors.isLoadingDashboard));
    this.dashboard$ = this.store.pipe(select(HomeSelectors.getDashboard));
    this.visualContents$ = this.store.pipe(select(HomeSelectors.getVisualContents));

    this.visualId$ = this.store.pipe(select(HomeSelectors.getVisualId));

    this.visual$ = this.store.pipe(select(HomeSelectors.getVisual));
    this.loadingVisual$ = this.store.pipe(select(HomeSelectors.isLoadingVisual))

    this.loaded$ = this.store.select(HomeSelectors.loaded);

    this.userState$ = this.store.select(AppSelectors.getUserState);

    this.copyingDashboard$ = this.store.select(HomeSelectors.isCopyingDashboard);
    this.addingDashboard$ = this.store.select(HomeSelectors.isAddingDashboard);

    this.focusedVisual$ = this.store.pipe(select(HomeSelectors.getFocusedVisual));
    this.hasFocusedVisual$ = this.store.pipe(select(HomeSelectors.hasFocusedVisual));

    this.loading$ = combineForLoading(
      this.loadingDashboards$,
      this.loadingUserState$,
      this.loadingVisuals$,
      this.loaded$.pipe(map(l => !l))
    );

    this.busy$ = combineLogicalOr(
      this.loading$,
      this.loadingDashboard$,
      this.copyingDashboard$,
      this.addingDashboard$,
      this.loadingVisuals$
    );

    this.store.dispatch(HomeActions.load({}));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }


  copyDashboard(dashboardId: number) {
    this.store.dispatch(HomeActions.copyDashboard({ dashboardId }));
  }

  editDashboard(e: { dashboardId: number }) {
    this.store.dispatch(HomeActions.editDashboard(e));
  }

  loadDashboard(dashboardId: number) {
    this.store.dispatch(HomeActions.loadDashboard({ dashboardId }));
  }

  loadVisual(visualId: number) {
    this.store.dispatch(HomeActions.loadVisual({ visualId }));
  }

  addDashboardToHome(dashboardId: number) {
    this.store.dispatch(HomeActions.addDashboard({ dashboardId }));
  }

  addVisualToHome(visualId: number) {
    this.store.dispatch(HomeActions.addVisual({ visualId }));
  }

  setFocusedVisual(visual: DashboardVisualTarget) {
    this.store.dispatch(HomeActions.setFocusedVisual({ visual }));
  }

  exportToPdf(filters: FilterDto[]) {
    this.store.dispatch(HomeActions.exportDashboardToPdf({ filters }));
  }

  copyVisual(visualId: number) {
    this.store.dispatch(HomeActions.copyVisual({ visualId }));
  }

  exportVisual() {
    this.store.dispatch(HomeActions.exportVisual({ filters: null }));
  }
}
